import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import ReactSelect from '../../../../components/ReactSelect';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { GRID_SELECT } from '../../GscTools/selectStyles';
import { contributorColumns } from '../../GscTools/columns';
import { reportsPosition } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';

/**
 * Block 03 — which search appearances this property returned, and the pages behind one of them.
 *
 * It is two requests in sequence, and the order matters. Search Console will not combine the
 * searchAppearance dimension with any other, so the appearances have to be discovered on their
 * own before one of them can be used as a filter; a hardcoded list of Google's result features
 * would offer appearances this property has never had and hide the ones it does.
 *
 * Only keys the discovery call actually returned are offered. One it did not is simply not in the
 * list — including after a change of dates, which can remove an appearance the property had in
 * the previous window: the selection is cleared and said to have been cleared, rather than left
 * naming a key the current window knows nothing about.
 */
class AppearanceContributors extends Component {
    constructor(props) {
        super(props);
        this.searchFunction = this.searchFunction.bind(this);
    }

    searchFunction(data, search) {
        if (!search) return data;
        const term = search.toLowerCase();
        return data.filter((item) => item.key?.toLowerCase().includes(term));
    }

    renderPicker() {
        const { t, appearances, selected, onSelect } = this.props;

        const options = appearances.map((item) => ({ value: item.key, label: item.key }));

        return (
            <div className="gsc-tool-criteria">
                {/* Two columns of the control grid rather than one: an appearance key is a long
                    enum, and a lone quarter-width select reads as a stray control. */}
                <div className="gsc-tool-field gsc-tool-field-wide">
                    <label>{t('tools.audience_appearance.appearance.discovery_label')}</label>
                    <ReactSelect
                        options={options}
                        value={options.find((item) => item.value === selected) ?? null}
                        isSearchable={true}
                        blurInputOnSelect={true}
                        classname="gsc-tool-select"
                        controlStyles={GRID_SELECT}
                        placeholder={t('tools.audience_appearance.appearance.discovery_choose')}
                        customeOnChange={(item) => onSelect(item.value)}
                    />
                    <span className="gsc-tool-field-note">
                        {t('tools.audience_appearance.appearance.discovery_note', {
                            count: appearances.length,
                        })}
                    </span>
                </div>
            </div>
        );
    }

    render() {
        const { t, scope, status, error, appearances, selected, cleared, pages, onClear } =
            this.props;

        const columns = contributorColumns(t, {
            hasPosition: reportsPosition(scope.searchType),
            keyLabel: t('tools.gsc.columns.page'),
            isUrl: true,
        });

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">
                            {t('tools.audience_appearance.appearance.title')}
                        </p>
                        <p className="gsc-tool-sub">
                            {t('tools.audience_appearance.appearance.description')} ·{' '}
                            {formatPeriod(scope.current)}
                        </p>
                    </div>

                    {!!selected && (
                        <div className="gsc-tool-icon-actions">
                            <button
                                type="button"
                                title={t('tools.audience_appearance.appearance.clear')}
                                aria-label={t('tools.audience_appearance.appearance.clear')}
                                onClick={onClear}
                            >
                                <i className="icon-x-close" />
                            </button>
                        </div>
                    )}
                </div>

                <div className="card-body">
                    {(status === 'idle' || status === 'loading') && <Loading padding={20} />}

                    {status === 'error' && (
                        <div className="gsc-tool-banner error">
                            {error ?? t('global.something_went_wrong')}
                        </div>
                    )}

                    {/* Above the list rather than inside it: a window that dropped the open
                        appearance has to say so even when it returned no appearances at all. */}
                    {cleared && (
                        <div className="gsc-tool-banner">
                            {t('tools.audience_appearance.appearance.cleared')}
                        </div>
                    )}

                    {status === 'done' && !appearances.length && (
                        <div className="gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-globe" />
                            </span>
                            <h4>{t('tools.audience_appearance.appearance.none_title')}</h4>
                            <p>{t('tools.audience_appearance.appearance.none')}</p>
                        </div>
                    )}

                    {status === 'done' && !!appearances.length && (
                        <>
                            {this.renderPicker()}

                            {!selected && (
                                <p className="gsc-tool-table-note">
                                    {t('tools.audience_appearance.appearance.idle')}
                                </p>
                            )}

                            {!!selected && pages?.status === 'loading' && <Loading padding={20} />}

                            {!!selected && pages?.status === 'error' && (
                                <div className="gsc-tool-banner error">
                                    {pages.error ?? t('global.something_went_wrong')}
                                </div>
                            )}

                            {!!selected && pages?.status === 'done' && (
                                <>
                                    <DataTable
                                        nodes={pages.rows}
                                        COLUMNS={columns}
                                        sortFns={getSortKeys(columns)}
                                        additionalStyles={setAdditionalStyles(columns)}
                                        searchFunction={this.searchFunction}
                                        fileName={t(
                                            'tools.audience_appearance.appearance.pages_file',
                                            { appearance: selected },
                                        )}
                                        customEmptyMessage={t(
                                            'tools.audience_appearance.appearance.pages_empty',
                                        )}
                                        customContainerStyles="gsc-tool-key-table"
                                    />

                                    <p className="gsc-tool-table-note">
                                        {t(
                                            'tools.audience_appearance.appearance.contributors_note',
                                        )}
                                    </p>
                                </>
                            )}

                            <p className="gsc-tool-table-note">
                                {t('tools.audience_appearance.appearance.partition_note')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(AppearanceContributors);
